"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Cog,
  FileText,
  Landmark,
  LayoutDashboard,
  ListChecks,
} from "lucide-react";
import { cn } from "@/lib/utils";

type Route = {
  href: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
};

type Group = {
  label: string;
  routes: Route[];
};

const GROUPS: Group[] = [
  {
    label: "KPMG",
    routes: [
      {
        href: "/",
        label: "KPMG briefing",
        description: "CEE TP partnership",
        icon: LayoutDashboard,
      },
      {
        href: "/requirements",
        label: "Requirements",
        description: "KPMG brief + analysis",
        icon: ClipboardList,
      },
      {
        href: "/first-meeting-questions",
        label: "First-meeting questions",
        description: "Ordered script + follow-ups",
        icon: ListChecks,
      },
      {
        href: "/summary",
        label: "Project summary",
        description: "PROJECT.MD",
        icon: FileText,
      },
    ],
  },
  {
    label: "ANRSC",
    routes: [
      {
        href: "/ANRSC",
        label: "ANRSC briefing",
        description: "Digitalization & AI",
        icon: Landmark,
      },
      {
        href: "/ANRSC/requirements",
        label: "Requirements & analysis",
        description: "ANRSC paper + our read",
        icon: ClipboardList,
      },
      {
        href: "/ANRSC/summary",
        label: "Project summary",
        description: "ANRSC.MD",
        icon: FileText,
      },
    ],
  },
  {
    label: "CNC-Quotation",
    routes: [
      {
        href: "/CNC-Quotation",
        label: "CNC-Quotation briefing",
        description: "Instant quoting from 3D",
        icon: Cog,
      },
      {
        href: "/CNC-Quotation/requirements",
        label: "Requirements & analysis",
        description: "Client brief + our read",
        icon: ClipboardList,
      },
      {
        href: "/CNC-Quotation/summary",
        label: "Project summary",
        description: "CNC-Quotation.MD",
        icon: FileText,
      },
    ],
  },
];

const STORAGE_KEY = "nav-shell-collapsed";

export function NavShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) setCollapsed(stored === "true");
  }, []);

  useEffect(() => {
    if (hydrated) localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed, hydrated]);

  if (pathname === "/login") {
    return <>{children}</>;
  }

  return (
    <>
      <aside
        className={cn(
          "fixed left-0 top-0 z-30 flex h-screen flex-col border-r border-zinc-200 bg-white transition-[width] duration-200 dark:border-zinc-800 dark:bg-zinc-950",
          collapsed ? "w-14" : "w-60",
        )}
        aria-label="Primary navigation"
      >
        <div className="flex h-14 items-center justify-between border-b border-zinc-200 px-3 dark:border-zinc-800">
          {!collapsed && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              Leads
            </span>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="ml-auto rounded p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!collapsed}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-4 overflow-y-auto p-2">
          {GROUPS.map((group) => (
            <div key={group.label} className="flex flex-col gap-1">
              {!collapsed && (
                <span className="px-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-600">
                  {group.label}
                </span>
              )}
              {collapsed && (
                <span className="mx-auto mb-0.5 h-px w-6 bg-zinc-200 dark:bg-zinc-800" />
              )}
              {group.routes.map(({ href, label, description, icon: Icon }) => {
                const active = pathname === href;
                return (
                  <Link
                    key={href}
                    href={href}
                    title={collapsed ? label : undefined}
                    className={cn(
                      "group flex items-center gap-3 rounded-md px-2 py-2 text-sm font-medium transition-colors",
                      active
                        ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                        : "text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800",
                      collapsed && "justify-center px-0",
                    )}
                  >
                    <Icon size={16} className="shrink-0" />
                    {!collapsed && (
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate leading-tight">{label}</span>
                        {description && (
                          <span
                            className={cn(
                              "truncate text-[11px] font-normal leading-tight",
                              active
                                ? "text-zinc-300 dark:text-zinc-600"
                                : "text-zinc-500",
                            )}
                          >
                            {description}
                          </span>
                        )}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {!collapsed && (
          <div className="border-t border-zinc-200 px-3 py-2 text-[11px] text-zinc-500 dark:border-zinc-800">
            Internal · gated by APP_PASSWORD
          </div>
        )}
      </aside>

      <div
        className={cn(
          "min-h-screen transition-[padding-left] duration-200",
          collapsed ? "pl-14" : "pl-60",
        )}
      >
        {children}
      </div>
    </>
  );
}
